import { useState, useEffect } from "react";
import { 
  Settings as SettingsIcon, 
  Save,
  Building,
  Percent,
  Target,
  DollarSign
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = ({ user }) => {
  const [settings, setSettings] = useState({
    company_name: "DopeInHD Incorporated",
    logo_url: "",
    tax_percentage: 25,
    weekly_deposit_goal: 5000,
    monthly_revenue_target: 25000
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/settings`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success("Settings saved");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Settings</h1>
        <p className="text-[#A1A1AA] mt-1">Configure your business settings and goals</p>
      </div>

      {/* Brand Settings */}
      <Card className="bg-[#121214] border-[#27272A]">
        <CardHeader>
          <CardTitle className="text-[#FAFAFA] flex items-center gap-2">
            <Building size={20} className="text-[#D4AF37]" />
            Brand Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-[#A1A1AA]">Company Name</Label>
            <Input
              value={settings.company_name}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
            />
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Logo URL</Label>
            <Input
              value={settings.logo_url}
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
              placeholder="https://..."
              className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
            />
          </div>
          {settings.logo_url && (
            <div className="p-4 bg-[#1A1A1D] rounded-lg">
              <p className="text-xs text-[#71717A] mb-2">Logo Preview</p>
              <img src={settings.logo_url} alt="Logo" className="h-16 w-auto" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Financial Settings */}
      <Card className="bg-[#121214] border-[#27272A]">
        <CardHeader>
          <CardTitle className="text-[#FAFAFA] flex items-center gap-2">
            <DollarSign size={20} className="text-[#D4AF37]" />
            Financial Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-[#A1A1AA] flex items-center gap-2">
              <Percent size={14} />
              Tax Reserve Percentage
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="100"
                value={settings.tax_percentage}
                onChange={(e) => setSettings({ ...settings, tax_percentage: parseFloat(e.target.value) || 0 })}
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] w-32"
              />
              <span className="text-[#A1A1AA]">%</span>
            </div>
            <p className="text-xs text-[#71717A] mt-1">
              Percentage of profit to set aside for taxes
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Goals */}
      <Card className="bg-[#121214] border-[#27272A]">
        <CardHeader>
          <CardTitle className="text-[#FAFAFA] flex items-center gap-2">
            <Target size={20} className="text-[#D4AF37]" />
            Revenue Goals
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-[#A1A1AA]">Weekly Bank Deposit Goal ($)</Label>
            <Input
              type="number"
              value={settings.weekly_deposit_goal}
              onChange={(e) => setSettings({ ...settings, weekly_deposit_goal: parseFloat(e.target.value) || 0 })}
              className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
            />
          </div>
          <div>
            <Label className="text-[#A1A1AA]">Monthly Revenue Target ($)</Label>
            <Input
              type="number"
              value={settings.monthly_revenue_target}
              onChange={(e) => setSettings({ ...settings, monthly_revenue_target: parseFloat(e.target.value) || 0 })}
              className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
            />
          </div>
        </CardContent>
      </Card>

      {/* User Info */}
      <Card className="bg-[#121214] border-[#27272A]">
        <CardHeader>
          <CardTitle className="text-[#FAFAFA]">Your Account</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-2 border-[#D4AF37]" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center text-2xl font-bold text-[#0A0A0B]">
                {user?.name?.charAt(0)}
              </div>
            )}
            <div>
              <p className="text-lg font-semibold text-[#FAFAFA]">{user?.name}</p>
              <p className="text-sm text-[#A1A1AA]">{user?.email}</p>
              <span className="text-xs px-2 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] mt-1 inline-block">
                {user?.role?.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button 
        onClick={handleSave} 
        className="btn-gold w-full md:w-auto"
        disabled={saving}
        data-testid="save-settings-btn"
      >
        {saving ? (
          <span className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-[#0A0A0B] border-t-transparent rounded-full animate-spin"></div>
            Saving...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Save size={18} />
            Save Settings
          </span>
        )}
      </Button>
    </div>
  );
};

export default Settings;
